import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type {
    ArticleSummary,
    Client,
    ProjectSummary,
    Service,
    Stat,
    TeamMember,
    Testimonial
} from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import HomeHero from '@/components/landing/home-hero'
import ImpactSection from '@/components/landing/impact-section'
import ServicesOverview from '@/components/landing/services-overview'
import SelectedWork from '@/components/landing/selected-work'
import AboutTeaser from '@/components/landing/about-teaser'
import InsightsPreview from '@/components/landing/insights-preview'
import ClientStrip from '@/components/sections/client-strip'
import ProcessSection from '@/components/sections/process-section'
import TeamSection from '@/components/sections/team-section'
import TestimonialsSection from '@/components/sections/testimonials-section'
import FinalCta from '@/components/sections/final-cta'

import {
    REVALIDATE_SECONDS,
    getArticles,
    getClients,
    getProjects,
    getServices,
    getStats,
    getTeam,
    getTestimonials
} from '@/lib/content'

interface HomeProps {
    clients: Client[]
    stats: Stat[]
    services: Service[]
    projects: ProjectSummary[]
    team: TeamMember[]
    testimonials: Testimonial[]
    articles: ArticleSummary[]
}

const Home: FC<HomeProps> = ({
    clients = [],
    stats = [],
    services = [],
    projects = [],
    team = [],
    testimonials = [],
    articles = []
}): ReactNode => {
    return (
        <Layout>
            <Seo description="BLITZ is a creative marketing agency. We make brands impossible to ignore with bold strategy, creative campaigns and performance marketing that delivers measurable growth." />

            <HomeHero />
            <ClientStrip clients={clients} />
            <ImpactSection stats={stats} />
            <ServicesOverview services={services} />
            <SelectedWork projects={projects} />
            <ProcessSection />
            <AboutTeaser />
            <TeamSection team={team} label='The team / 05' />
            <TestimonialsSection testimonials={testimonials} label='Client stories / 06' />
            <InsightsPreview articles={articles} />
            <FinalCta />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [clients, stats, services, projects, team, testimonials, articles] = await Promise.all([
        getClients(),
        getStats(),
        getServices(),
        getProjects(),
        getTeam(),
        getTestimonials(),
        getArticles()
    ])

    return {
        props: {
            clients,
            stats,
            services,
            projects: projects.slice(0, 4),
            team,
            testimonials,
            articles: articles.slice(0, 3)
        },
        revalidate: REVALIDATE_SECONDS
    }
}

export default Home
